/**
 * Generate a stable Idempotency-Key for a mutation. The same key must be
 * reused across retries of one logical submit so the server replays the
 * original response (see internal/api/handlers idempotency helpers).
 */
export function newIdempotencyKey(): string {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
		return crypto.randomUUID();
	}
	// Fallback for environments without crypto.randomUUID.
	return "idk-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Header bag carrying an Idempotency-Key, spread into a request's headers. */
export function idempotencyHeader(key?: string): Record<string, string> {
	return { "Idempotency-Key": key ?? newIdempotencyKey() };
}

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const AUTOMATIC_IDEMPOTENCY_EXCEPTIONS = new Set([
	"/api/v1/auth/login",
	"/api/v1/auth/refresh",
	"/api/v1/auth/logout",
	"/admin/v1/agents/migrations/preview",
	"/admin/v1/nodes/provisioning-bundles",
	"/admin/v1/nodes/{nodeID}/provisioning-bundles",
	"/admin/v1/nodes/{nodeID}/adapter-tokens",
	"/admin/v1/nodes/{nodeID}/adapter-tokens/{tokenID}/rotate",
	"/admin/v1/enrollment-tokens",
	"/admin/v1/enrollment-tokens/verify",
	"/api/v1/subscription-links/{linkID}/rotate-secret",
	"/internal/v1/node/manifest/ack",
	"/internal/v1/node/manifest/heartbeat",
	"/internal/v1/node/boot/close",
	"/internal/v1/node/usage-reports",
	"/api/v1/agents/{agent_id}/install-report",
	"/api/v1/agents/{agent_id}/heartbeats",
	"/api/v1/nodes/{node_id}/traffic-reports",
	"/api/v1/nodes/{node_id}/heartbeats",
	"/api/v1/nodes/{node_id}/install-report",
	"/api/v1/nodes/{node_id}/adapter-token/rotate",
]);

/**
 * Return whether the SPA client should add a generic Idempotency-Key.
 * Exceptions either do not mutate state or return a secret that the generic
 * idempotency store must never persist or replay.
 */
export function shouldAutomaticallyAttachIdempotencyKey(
	method: string,
	schemaPath: string,
): boolean {
	return (
		MUTATION_METHODS.has(method.toUpperCase()) &&
		!AUTOMATIC_IDEMPOTENCY_EXCEPTIONS.has(schemaPath)
	);
}

/** Add a generated key without replacing an explicitly supplied one. */
export function ensureIdempotencyKey(
	request: Request,
	schemaPath: string,
	keyFactory: () => string = newIdempotencyKey,
): void {
	if (
		shouldAutomaticallyAttachIdempotencyKey(request.method, schemaPath) &&
		!request.headers.has("Idempotency-Key")
	) {
		request.headers.set("Idempotency-Key", keyFactory());
	}
}
